import { createContext, useContext, type FC, type PropsWithChildren } from 'react';
import { type UserFeatures } from '@matrix-forge/graphql-client';

/**
 * Context for accessing user feature flags throughout the application.
 * This context is populated by the studio app's UserInfoProvider and made
 * available to UI components without requiring them to import from studio.
 */
export const FeatureFlagsContext = createContext<Partial<UserFeatures>>({});

/**
 * Hook to access feature flags.
 * Returns an empty object if used outside of FeatureFlagsProvider.
 *
 * @example
 * const features = useFeatureFlags();
 * const isEnabled = features.devoToolsEnabled ?? false;
 */
export const useFeatureFlags = () => {
  return useContext(FeatureFlagsContext);
};

type FeatureFlagsProviderProps = PropsWithChildren<{
  features?: Partial<UserFeatures>;
}>;

/**
 * Provider component that makes feature flags available to child components.
 * Should be rendered near the root of the application, after authentication.
 *
 * @example
 * <FeatureFlagsProvider features={userInfo?.features || {}}>
 *   <App />
 * </FeatureFlagsProvider>
 */
export const FeatureFlagsProvider: FC<FeatureFlagsProviderProps> = ({
  features = {},
  children,
}) => {
  return <FeatureFlagsContext.Provider value={features}>{children}</FeatureFlagsContext.Provider>;
};

FeatureFlagsProvider.displayName = 'FeatureFlagsProvider';
